import { NetworkStatusService } from '@library/services/qp-network-status/qp-network-status.service';
import { INTERCEPTOR_SKIP_HEADER } from '@one/app/shared/interceptors/inspection.interceptor';
import { ApiConnectivityStatusTracker } from '@one/app/shared/services/api-connectivity-probe/api-connectivity-probe.service';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, tap } from 'rxjs';

/** Dummy base used to resolve relative request URLs (e.g. `api/inspections/1`) to inspect their `pathname`. */
const URL_RESOLUTION_BASE = 'https://qima-api-connectivity.invalid/';
/** Network drop / gateway blips (Cloudflare 499 often surfaces as these). Any other status means the API answered. */
const TRANSIENT_NETWORK_STATUS = [0, 502, 503, 504];

/**
 * @description
 * Whether the request targets our backend, based on its resolved `pathname` (`/api/...`).
 * @param {string} url the request URL, relative or absolute
 * @returns {boolean} true if this is a backend `/api/` call
 */
function isApiRequest(url: string): boolean {
  return new URL(url, URL_RESOLUTION_BASE).pathname.startsWith('/api/');
}

/**
 * @description
 * Whether the app is offline because of the manual toggle or device connectivity.
 * Outcomes recorded while offline would be read as a poor connection instead of an offline state.
 * @param {NetworkStatusService} networkStatusService the network status source
 * @returns {boolean} true when nothing should be recorded
 */
function isOffline(networkStatusService: NetworkStatusService): boolean {
  return !networkStatusService.isOnline || !networkStatusService.isBrowserOnline;
}

/**
 * @description
 * Feeds the inspector connection badge from every backend call, not only from the offline actions queue.
 * Without this, a poor-connection (orange) badge could only recover once a queued action or the
 * `api/info` probe succeeded, so it stayed orange while plain reads were already going through.
 *
 * Records into {@link ApiConnectivityStatusTracker}, which does not inject `HttpClient`.
 * Injecting the probe service (and therefore `HttpClient`) from an interceptor can stall the chain
 * so callers never get `complete` (home `inspections-list--skeleton` stays up because `isLoading` never clears).
 *
 * Must be registered LAST among the functional interceptors: the offline domain interceptors answer from
 * IndexedDB without calling `next`, so a locally served response never reaches this interceptor and cannot
 * be mistaken for API reachability.
 *
 * Requests carrying {@link INTERCEPTOR_SKIP_HEADER} are ignored: queued sync actions and the reachability
 * probe already report their own outcome, and counting them twice would trip the forced-offline threshold early.
 * @param {HttpRequest} request the request to intercept
 * @param {HttpHandlerFn} next the request handler
 * @returns {Observable<HttpEvent<unknown>>} the request outcome, untouched
 */
export function apiConnectivityStatusInterceptor$(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (!isApiRequest(request.url) || request.headers.has(INTERCEPTOR_SKIP_HEADER)) {
    return next(request);
  }

  const apiConnectivityStatusTracker: ApiConnectivityStatusTracker = inject(ApiConnectivityStatusTracker);
  const networkStatusService: NetworkStatusService = inject(NetworkStatusService);

  return next(request).pipe(
    tap({
      next(event: HttpEvent<unknown>): void {
        if (!(event instanceof HttpResponse) || isOffline(networkStatusService)) {
          return;
        }

        apiConnectivityStatusTracker.recordSuccess();
      },
      error(error: unknown): void {
        if (isOffline(networkStatusService)) {
          return;
        }

        const status = error instanceof HttpErrorResponse ? error.status : 0;

        if (TRANSIENT_NETWORK_STATUS.includes(status)) {
          apiConnectivityStatusTracker.recordFailure();
        } else {
          // The API answered (validation, auth, not found…), so connectivity itself is fine.
          apiConnectivityStatusTracker.recordSuccess();
        }
      },
    })
  );
}
